<template>
  <v-dialog
    :value="show"
    persistent
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    transition="dialog-bottom-transition"
    :width="$store.state.common.largeDialogWidth"
  >
    <v-card>
      <v-card-title class="headline">
        {{ $t('firstloginDialogTitle') }}
      </v-card-title>
      <v-divider />
      <v-card-text>
        <!-- Password Widget -->
        <v-slide-y-transition>
          <div v-if="$userprofile && passwordNeeded">
            <div class="mt-2 subheading grey--text text--darken-3">
              {{ $t('firstloginPleaseSetCustomPassword') }}:
            </div>
            <v-layout
              align-center
              class="mt-2"
              wrap
            >
              <v-layout
                column
                class="mt-2"
              >
                <div class="mb-2">
                  {{ $t('passwordRuleTitle') }}:
                </div>
                <v-layout align-center>
                  <v-icon
                    v-if="passwordRuleChars"
                    small
                    color="success"
                  >
                    check
                  </v-icon>
                  <v-icon
                    v-else
                    small
                    color="error"
                  >
                    clear
                  </v-icon>
                  <span class="ml-2">{{ $t('passwordRuleChars') }}</span>
                </v-layout>
                <v-layout align-center>
                  <v-icon
                    v-if="passwordRuleNoSpaces"
                    small
                    color="success"
                  >
                    check
                  </v-icon>
                  <v-icon
                    v-else
                    small
                    color="error"
                  >
                    clear
                  </v-icon>
                  <span class="ml-2">{{ $t('passwordRuleNoSpaces') }}</span>
                </v-layout>
                <v-layout align-center>
                  <v-icon
                    v-if="passwordRuleMinLength"
                    small
                    color="success"
                  >
                    check
                  </v-icon>
                  <v-icon
                    v-else
                    small
                    color="error"
                  >
                    clear
                  </v-icon>
                  <span class="ml-2">{{ $t('passwordRuleMinLength') }}</span>
                </v-layout>
                <v-layout align-center>
                  <v-icon
                    v-if="passwordRuleBothMatch"
                    small
                    color="success"
                  >
                    check
                  </v-icon>
                  <v-icon
                    v-else
                    small
                    color="error"
                  >
                    clear
                  </v-icon>
                  <span class="ml-2">{{ $t('passwordRuleBothMatch') }}</span>
                </v-layout>
              </v-layout>
              <v-layout
                column
                :class="{'mt-3': $vuetify.breakpoint.xs}"
              >
                <v-layout align-center>
                  <v-text-field
                    v-model="password1"
                    class="mr-2"
                    :type="showPassword ? 'text' : 'password'"
                    :label="$t('password')"
                    :disabled="busy"
                    autocomplete="new-password"
                  />
                  <v-btn
                    icon
                    small
                    class="mx-0 my-0"
                    :title="$t('showPasswords')"
                    @click="showPassword = !showPassword"
                  >
                    <v-icon
                      v-if="showPassword"
                      color="msr"
                    >
                      visibility
                    </v-icon>
                    <v-icon
                      v-else
                      color="grey"
                    >
                      visibility_off
                    </v-icon>
                  </v-btn>
                </v-layout>
                <v-text-field
                  v-model="password2"
                  class="mr-2"
                  style="padding-right: 28px"
                  :type="showPassword ? 'text' : 'password'"
                  :label="$t('passwordRepeat')"
                  :disabled="busy"
                  autocomplete="new-password"
                />
              </v-layout>
            </v-layout>
          </div>
        </v-slide-y-transition>
        <!-- DSGVO Compliance -->
        <v-slide-y-transition>
          <div
            v-if="$userprofile && dsgvoComplianceNeeded"
            class="mt-4"
          >
            <v-divider
              v-if="$userprofile && passwordNeeded"
              class="mb-3"
            />
            <div class="subheading mb-2">
              {{ $t('pleaseConfirmDataPrivacy') }}:
            </div>
            <dsgvo-compliance
              :accepted.sync="dsgvoAccepted"
              flat
              no-padding
              hide-title
              :disabled="busy"
            />
          </div>
        </v-slide-y-transition>
      </v-card-text>
      <v-card-actions class="pb-3">
        <v-layout justify-center>
          <btn-abort
            :label="$t('logout')"
            round
            @click="abort()"
          />
          <v-btn
            color="msr"
            :dark="formIsValid"
            :disabled="!formIsValid"
            :loading="busy"
            round
            @click="submit()"
          >
            {{ $t('confirm') }}
          </v-btn>
        </v-layout>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import BtnAbort from '@/components/ButtonAbort';
import DsgvoCompliance from '@/components/DsgvoCompliance';

export default {
  components: {
    BtnAbort,
    DsgvoCompliance,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      busy: false,
      showPassword: false,
      password1: '',
      password2: '',
      dsgvoAccepted: false,
    };
  },
  computed: {
    passwordRuleMinLength() {
      return this.password1.length >= 8;
    },
    passwordRuleChars() {
      return /[a-zA-Z]+/.test(this.password1) && /[0-9]+/.test(this.password1);
    },
    passwordRuleNoSpaces() {
      return this.password1.length > 0 && !/\s+/.test(this.password1);
    },
    passwordRuleBothMatch() {
      return (
        this.password1.length > 0 &&
        this.password2.length > 0 &&
        this.password1 === this.password2
      );
    },
    passwordIsValid() {
      if (!this.passwordNeeded) {
        return true;
      }
      return (
        this.passwordRuleMinLength &&
        this.passwordRuleNoSpaces &&
        this.passwordRuleBothMatch &&
        this.passwordRuleChars
      );
    },
    passwordNeeded() {
      return (
        this.$store.getters.formRedirectEnabled &&
        this.$store.getters.formRedirectNeeded
      );
    },
    dsgvoComplianceNeeded() {
      return (
        this.$store.getters.dsgvoRedirectEnabled &&
        this.$store.getters.dsgvoRedirectNeeded
      );
    },
    formIsValid() {
      return (
        this.passwordIsValid &&
        (this.dsgvoComplianceNeeded ? this.dsgvoAccepted : true)
      );
    },
  },
  watch: {
    show: function (show) {
      if (show) {
        this.dsgvoAccepted = this.$userprofile
          ? this.$userprofile.dsgvo_compliance_given_at !== null
          : false;
      }
    },
  },
  methods: {
    abort() {
      this.$api.logout();
      this.$router.push('/login');
    },
    submit() {
      if (!this.formIsValid) {
        return;
      }
      this.busy = true;
      this.$api
        .submitFirstloginForm(
          this.password1,
          this.password2,
          this.dsgvoAccepted
        )
        .then(() => {
          this.$store.dispatch('RETRIEVE_CURRENT_USER').then(() => {
            this.busy = false;
            this.$router.push('/dashboard');
          });
        })
        .catch(() => {
          this.busy = false;
        });
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/FirstLoginDialog.vue